// ============================================================================
// Sidebar Component
// ============================================================================

import { auth } from 'app/auth/auth.js';
import {
    uploadAvatar,
    removeAvatar,
    addSidebarCategory,
    renameSidebarCategory,
    deleteSidebarCategory,
    updateSidebarConfig,
    getSidebarConfig
} from 'shared/data/profile-api.js';
import { showToast } from 'shared/toast/toast.js';
import { withButtonLoading } from 'shared/utils/button-loading.js';
import { notificationsPanel } from 'app/shell/notifications-panel/notifications-panel.js';
import { escapeHtml, renderIcon } from 'shared/utils/dom-helpers.js';
import { debounce } from 'shared/utils/shared.js';
import { getTaskListsByIds } from 'features/lists/data/lists-api.js';
import { taskListPath } from 'features/lists/utils/list-slug.js';
import { modal } from 'shared/components/modal/modal.js';
import { PROGRAMMED_LISTS } from 'features/lists/config/programmed-lists.js';
import { canAccessProgrammedList } from 'shared/utils/permissions.js';
import { resourceIcons } from 'shared/components/resource-icons/resource-icons.js';
import { EventManager } from 'shared/utils/event-manager.js';
import { isDesktopShell } from 'shared/utils/desktop-shell.js';
import { getVersionString, getRecentChanges } from 'config/version.js';
import { renderAeriumLogo } from 'shared/icons/aerium-logo.js';
// Shared dropdown chrome (positioning + outside-click + escape). The sidebar
// passes baseClass:false so the menu element carries ONLY its own self-contained
// .sidebar__action-menu class (not the data-grid base class), keeping it
// independent of the globally-loaded data-grid stylesheet.
import { createMenu } from 'shared/components/data-grid/modules/menu-factory.js';
import { toLayoutPx, rectToLayout } from 'shared/utils/zoom.js';
// IconPicker is imported lazily (see _beginNewGroup / _beginRenameGroup) so the
// ~1500-icon Font Awesome catalogue it pulls in stays out of the app boot bundle.

const PINNED_GROUP_ID = 'pinned';

export const sidebar = {
    pinnedTaskLists: [],
    isCollapsed: false,
    events: new EventManager('Sidebar'),

    async load() {
        const container = document.getElementById('sidebar');
        if (!container) {
            console.error('[Sidebar] Container not found!');
            return;
        }

        // Tear down any previous instance (idempotent on first load) so
        // re-loading the sidebar doesn't accumulate listeners or duplicate
        // panel/icon initialization.
        this.dispose();

        // Load collapsed state from localStorage
        this.isCollapsed = localStorage.getItem('sidebar-collapsed') === 'true';
        this.categoryCollapseStates = JSON.parse(
            localStorage.getItem('sidebar-category-collapsed') || '{}'
        );

        // Render shell first so the skeleton has a home while pinned lists load
        container.innerHTML = this.template();
        this.attachEventListeners();
        this.syncCollapsedState();
        this.updateActiveRoute();
        this.setupNameTruncation();

        // Initialize shared resource icons
        resourceIcons.init(document.getElementById('sidebar-resource-icons'));

        // Show skeleton while loading pinned lists; swap in real items or error state
        this._renderPinnedSkeleton();
        try {
            await this.loadPinnedTaskLists();
            this.renderCustomMenuItems();
        } catch (error) {
            console.error('[Sidebar] Failed to load pinned task lists:', error);
            this._renderPinnedError();
        }
    },

    // Helper retained for readability at call sites; delegates to EventManager.
    _addListener(element, event, handler) {
        this.events.on(element, event, handler);
    },

    // Derived from shared PROGRAMMED_LISTS (only fields needed for sidebar rendering)
    programmedLists: PROGRAMMED_LISTS.map(({ id, name, icon, programmed_route }) => ({
        id, name, icon, programmed_route
    })),

    // Category collapse states: { categoryId: boolean }
    categoryCollapseStates: {},

    async loadPinnedTaskLists() {
        // Use cached pinned_task_lists from auth.profile (loaded during auth.check())
        const pinnedIds = auth.profile?.pinned_task_lists || [];

        if (pinnedIds.length === 0) {
            this.pinnedTaskLists = [];
            return;
        }

        // Filter out programmed list IDs to avoid unnecessary DB query
        const programmedIds = new Set(this.programmedLists.map(p => p.id));
        const dbPinnedIds = pinnedIds.filter(id => !programmedIds.has(id));

        // Fetch only the pinned task lists by ID (optimized query)
        let dbTaskLists = [];
        if (dbPinnedIds.length > 0) {
            const result = await getTaskListsByIds(dbPinnedIds);
            if (!result.success) {
                throw new Error(result.error || 'Failed to load pinned task lists');
            }
            dbTaskLists = result.data || [];
        }

        // Combine database lists with programmed lists
        const allLists = [...dbTaskLists, ...this.programmedLists];

        // Build lookup map for O(1) access
        const listMap = new Map(allLists.map(d => [d.id, d]));

        // Preserve pinned order, filtering out stale IDs
        this.pinnedTaskLists = pinnedIds
            .map(id => listMap.get(id))
            .filter(d => d !== undefined);

        // Auto-clean stale IDs from profile to free up pin slots
        const resolvedIds = this.pinnedTaskLists.map(d => d.id);
        if (resolvedIds.length < pinnedIds.length && auth.profile) {
            auth.profile.pinned_task_lists = resolvedIds;
            const { updatePinnedTaskLists } = await import('features/lists/data/lists-api.js');
            updatePinnedTaskLists(resolvedIds);
        }
    },

    async refreshPinnedTaskLists() {
        try {
            await this.loadPinnedTaskLists();
            this.renderCustomMenuItems();
        } catch (error) {
            console.error('[Sidebar] Error refreshing pinned task lists:', error);
            this._renderPinnedError();
        }
    },

    _renderPinnedSkeleton() {
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;

        const skeletonItems = Array.from({ length: 3 }).map(() => `
            <li class="sidebar__nav-item sidebar__nav-item--skeleton">
                <span class="sidebar__skeleton-icon" aria-hidden="true"></span>
                <span class="sidebar__skeleton-text" aria-hidden="true"></span>
            </li>
        `).join('');

        container.innerHTML = `
            <ul class="sidebar__nav sidebar__nav--custom" aria-busy="true" aria-label="Loading pinned lists">
                ${skeletonItems}
            </ul>
        `;
    },

    _renderPinnedError() {
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;

        container.innerHTML = `
            <div class="sidebar__pinned-error" role="alert">
                <i class="fas fa-exclamation-triangle sidebar__pinned-error-icon"></i>
                <span class="sidebar__pinned-error-text">Couldn't load pinned lists</span>
                <button type="button" class="sidebar__pinned-error-retry" data-action="retry-pinned" title="Retry">
                    <i class="fas fa-rotate-right"></i>
                </button>
            </div>
        `;

        const retryBtn = container.querySelector('[data-action="retry-pinned"]');
        if (retryBtn) {
            this._addListener(retryBtn, 'click', async () => {
                this._renderPinnedSkeleton();
                try {
                    await this.loadPinnedTaskLists();
                    this.renderCustomMenuItems();
                } catch (error) {
                    console.error('[Sidebar] Retry failed:', error);
                    this._renderPinnedError();
                }
            });
        }
    },

    renderCustomMenuItems() {
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;

        // Re-rendering replaces the container HTML, which would orphan an open
        // inline-edit picker's document listeners - destroy it first.
        this._destroyActiveInlinePicker();

        // Likewise close any open group "more options" dropdown so its anchor
        // (about to be replaced) and document-level listeners don't dangle.
        if (this._sectionMenu) this._sectionMenu.destroy();

        // Read sidebar config for category assignments
        const config = auth.profile?.sidebar_config || { categories: [], assignments: {}, listOrders: {} };
        const { categories = [], assignments = {} } = config;
        const listOrders = config.listOrders || {};

        // Hide pinned programmed lists the current user may not access (e.g. a
        // stale Billing pin on a non-management profile). Display-only: their
        // pinned_task_lists is left intact, so access is restored automatically
        // if the role is later granted.
        const accessiblePins = this.pinnedTaskLists.filter(
            t => canAccessProgrammedList(t.id, auth.profile)
        );

        // Render nothing if there are no pinned items AND no user categories
        if (accessiblePins.length === 0 && categories.length === 0) {
            container.innerHTML = '';
            return;
        }

        // Sort comparator using listOrders (fallback to pinned array order)
        const sortByOrder = (a, b) => {
            const orderA = listOrders[a.id] ?? 9999;
            const orderB = listOrders[b.id] ?? 9999;
            return orderA - orderB;
        };

        // Group items by category
        const categoryMap = new Map(); // categoryId -> items[]
        const pinnedItems = []; // uncategorized items

        for (const taskList of accessiblePins) {
            const catId = assignments[taskList.id];
            if (catId && categories.some(c => c.id === catId)) {
                if (!categoryMap.has(catId)) categoryMap.set(catId, []);
                categoryMap.get(catId).push(taskList);
            } else {
                pinnedItems.push(taskList);
            }
        }

        // Build ordered sections: every user category (even empty so they're drop targets), then Pinned
        const sortedCategories = [...categories].sort((a, b) => a.order - b.order);
        const sections = [];

        for (const cat of sortedCategories) {
            const items = (categoryMap.get(cat.id) || []).sort(sortByOrder);
            sections.push({ id: cat.id, name: cat.name, icon: cat.icon, items, isUserCategory: true });
        }

        // Pinned bucket: shown if it has items, OR if user categories exist (so users can drag to uncategorize)
        if (pinnedItems.length > 0 || sortedCategories.length > 0) {
            sections.push({
                id: PINNED_GROUP_ID,
                name: 'PINNED',
                items: pinnedItems.sort(sortByOrder),
                isUserCategory: false
            });
        }

        const sectionsHtml = sections.map(section => this._renderSection(section)).join('');
        container.innerHTML = `${sectionsHtml}${this._renderAddGroupFooter()}`;

        this.updateActiveRoute();
    },

    _renderSection(section) {
        const isCollapsed = this._isSectionCollapsed(section.id);
        const labelCollapsedClass = isCollapsed ? 'sidebar__section-label--collapsed' : '';
        const navCollapsedClass = isCollapsed ? 'sidebar__nav--collapsed' : '';
        const isPinned = section.id === PINNED_GROUP_ID;

        // The uncategorized PINNED bucket is hidden when empty (no clutter) but
        // revealed during an item drag so lists can still be dragged out of a
        // group. These modifiers drive that CSS behaviour (see shell.css).
        const isPinnedEmpty = isPinned && section.items.length === 0;
        const pinnedEmptyLabelClass = isPinnedEmpty ? 'sidebar__section-label--pinned-empty' : '';
        const pinnedEmptyNavClass = isPinnedEmpty ? 'sidebar__nav--pinned-empty' : '';

        const itemsHtml = section.items.map(taskList => {
            // Aliased lists link to their friendly path (#/afs); the rest fall
            // back to the canonical id form. data-route mirrors the path (minus
            // any query) so the active-state matcher highlights correctly.
            const route = taskList.programmed_route || taskListPath(taskList);
            const dataRoute = taskList.programmed_route
                || (taskList.slug ? `/${taskList.slug}` : '/list-view');

            return `
                <li class="sidebar__nav-item sidebar__nav-item--draggable"
                    data-list-id="${escapeHtml(taskList.id)}"
                    data-group-id="${escapeHtml(section.id)}"
                    draggable="true">
                    <a href="#${route}"
                       class="sidebar__nav-link"
                       data-route="${dataRoute}"
                       data-tooltip="${escapeHtml(taskList.name)}"
                       draggable="false">
                        ${renderIcon(taskList.icon || 'fa-list', 'sidebar__nav-icon')}
                        <span class="sidebar__nav-text">${escapeHtml(taskList.name)}</span>
                    </a>
                </li>
            `;
        }).join('');

        const emptyPlaceholder = section.items.length === 0
            ? `<li class="sidebar__nav-item sidebar__nav-item--empty" aria-hidden="true">
                   <span class="sidebar__nav-empty-text">${isPinned ? 'No uncategorized lists' : 'Drop a list here'}</span>
               </li>`
            : '';

        // Section header: user categories get a single "more options" button that
        // opens a Rename/Delete dropdown; the locked PINNED bucket has no actions.
        const headerActions = section.isUserCategory
            ? `
                <button type="button" class="sidebar__section-action"
                        data-section-action="menu"
                        data-category-id="${escapeHtml(section.id)}"
                        title="Group options" aria-haspopup="menu" aria-expanded="false" draggable="false">
                    <i class="fas fa-ellipsis-vertical"></i>
                </button>
              `
            : '';

        const draggableAttr = section.isUserCategory ? 'draggable="true"' : '';
        const labelDraggableClass = section.isUserCategory ? 'sidebar__section-label--draggable' : '';

        // User categories show a subtle group icon before the name; the locked
        // PINNED bucket keeps its plain look (no icon).
        const sectionIconHtml = section.isUserCategory
            ? renderIcon(section.icon || 'fa-folder', 'sidebar__section-icon')
            : '';

        return `
            <div class="sidebar__section-label sidebar__section-label--collapsible ${labelDraggableClass} ${labelCollapsedClass} ${pinnedEmptyLabelClass}"
                 data-category-id="${escapeHtml(section.id)}"
                 ${draggableAttr}>
                ${sectionIconHtml}
                <span class="sidebar__section-label-text">${escapeHtml(section.name)}</span>
                <div class="sidebar__section-actions">
                    ${headerActions}
                    <button type="button" class="sidebar__section-toggle"
                            data-category-toggle="${escapeHtml(section.id)}"
                            title="${isCollapsed ? 'Expand' : 'Collapse'}" draggable="false">
                        <i class="fas fa-chevron-down"></i>
                    </button>
                </div>
            </div>
            <ul class="sidebar__nav sidebar__nav--custom ${navCollapsedClass} ${pinnedEmptyNavClass}"
                data-category-nav="${escapeHtml(section.id)}">
                ${itemsHtml}${emptyPlaceholder}
            </ul>
        `;
    },

    _renderAddGroupFooter() {
        return `
            <div class="sidebar__add-group" data-add-group>
                <button type="button" class="sidebar__add-group-btn" data-action="new-group">
                    <i class="fas fa-plus"></i>
                    <span class="sidebar__add-group-text">New group</span>
                </button>
            </div>
        `;
    },

    _getAvatarData() {
        const profile = auth.profile;
        const user = auth.user;
        const userName = profile?.full_name || user?.user_metadata?.full_name || user?.email?.split('@')[0] || 'User';
        return {
            userName,
            userEmail: user?.email || '',
            initials: userName.charAt(0).toUpperCase(),
            avatarUrl: profile?.avatar_url
        };
    },

    template() {
        const { userName, initials, avatarUrl } = this._getAvatarData();

        // Parse name parts for progressive display
        const nameParts = userName.trim().split(/\s+/);
        const firstName = nameParts[0] || 'User';

        // Render avatar - image if available, initials otherwise
        const avatarContent = avatarUrl
            ? `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(userName)}" class="sidebar__avatar-img" />`
            : initials;

        return `
            <!-- Logo (click to toggle sidebar). The version tag beneath the name
                 is its own link: hover shows recent changelog, click opens About. -->
            <div class="sidebar__header">
                <div class="sidebar__logo" id="sidebar-logo" title="Toggle sidebar">
                    ${renderAeriumLogo('sidebar__logo-icon')}
                    <div class="sidebar__logo-text">
                        <span class="sidebar__logo-name">AERIUM</span>
                        <a class="sidebar__version" id="sidebar-version" href="#/settings?tab=about"
                           title="" aria-label="Version ${getVersionString()}, view changelog and About">${getVersionString()}</a>
                    </div>
                </div>
            </div>

            <!-- Scrollable Navigation Container -->
            <div class="sidebar__scroll-container">
                <!-- Main Navigation -->
                <ul class="sidebar__nav">
                    <li class="sidebar__nav-item">
                        <a href="#/" class="sidebar__nav-link sidebar__nav-link--active" data-route="/" data-tooltip="Dashboard">
                            <i class="fas fa-gauge-high sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Dashboard</span>
                        </a>
                    </li>
                    <li class="sidebar__nav-item">
                        <a href="#/clients" class="sidebar__nav-link" id="sidebar-clients-link" data-route="/clients" data-tooltip="Clients">
                            <i class="fas fa-address-book sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Clients</span>
                        </a>
                    </li>
                    <li class="sidebar__nav-item">
                        <a href="#/timesheet" class="sidebar__nav-link" data-route="/timesheet" data-tooltip="Timesheet">
                            <i class="fas fa-hourglass-half sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Timesheet</span>
                        </a>
                    </li>
                    <li class="sidebar__nav-item">
                        <a href="#/portfolio" class="sidebar__nav-link" data-route="/portfolio" data-tooltip="Portfolio">
                            <i class="fas fa-briefcase sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Portfolio</span>
                        </a>
                    </li>
                    <li class="sidebar__nav-item">
                        <a href="#/lists" class="sidebar__nav-link" data-route="/lists" data-tooltip="Lists">
                            <i class="fas fa-list-check sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Lists</span>
                        </a>
                    </li>
                    <li class="sidebar__nav-item">
                        <a href="#/calendar" class="sidebar__nav-link" data-route="/calendar" data-tooltip="Calendar">
                            <i class="fas fa-calendar-days sidebar__nav-icon"></i>
                            <span class="sidebar__nav-text">Calendar</span>
                        </a>
                    </li>
                </ul>

                <!-- Dynamic Category Sections (rendered by renderCustomMenuItems) -->
                <div id="sidebar-categories-container"></div>
            </div>

            <!-- User Section -->
            <div class="sidebar__user">
                <div class="sidebar__user-info">
                    <div class="sidebar__avatar sidebar__avatar--clickable" id="avatar-btn" title="Change profile picture">
                        ${avatarContent}
                    </div>
                    <div class="sidebar__user-details">
                        <div class="sidebar__user-name" data-full-name="${escapeHtml(userName)}" data-first-name="${escapeHtml(firstName)}">${escapeHtml(firstName)}</div>
                        <div class="sidebar__user-resources" id="sidebar-resource-icons"></div>
                    </div>
                </div>

                <div class="sidebar__user-actions">
                    <div class="notifications-bell-container" id="notifications-container"></div>
                    <button class="sidebar__action-btn" id="dev-tracker-btn" title="Dev Tracker">
                        <i class="fas fa-bug"></i>
                    </button>
                    <button class="sidebar__action-btn" id="settings-btn" title="Settings">
                        <i class="fas fa-cog"></i>
                    </button>
                    <button class="sidebar__action-btn sidebar__action-btn--logout" id="logout-btn" title="Logout">
                        <i class="fas fa-sign-out-alt"></i>
                    </button>
                </div>
            </div>
        `;
    },

    attachEventListeners() {
        const devTrackerBtn = document.getElementById('dev-tracker-btn');
        if (devTrackerBtn) {
            this._addListener(devTrackerBtn, 'click', () => window.location.hash = '#/dev-tracker');
        }

        const settingsBtn = document.getElementById('settings-btn');
        if (settingsBtn) {
            this._addListener(settingsBtn, 'click', () => window.location.hash = '#/settings');
        }

        const logoutBtn = document.getElementById('logout-btn');
        if (logoutBtn) {
            this._addListener(logoutBtn, 'click', async () => {
                // Icon-only button: withButtonLoading hides the icon + shows a centered
                // spinner pseudo-element while signing out. The redirect inside signOut()
                // is the success feedback, so no success toast. An error toast appears
                // only if signOut throws (it normally catches internally and still
                // redirects, which is fine).
                await withButtonLoading(logoutBtn, () => auth.signOut(), {
                    errorMessage: 'Failed to sign out'
                });
            });
        }

        const logo = document.getElementById('sidebar-logo');
        if (logo) {
            this._addListener(logo, 'click', () => this.toggleCollapsed());
        }

        // Version tag: hover/focus reveals a floating changelog tooltip; clicking
        // navigates to the About page (via its href). stopPropagation keeps the
        // click from bubbling to the logo's collapse toggle.
        const versionLink = document.getElementById('sidebar-version');
        if (versionLink) {
            this._addListener(versionLink, 'mouseenter', () => this._showVersionTooltip(versionLink));
            this._addListener(versionLink, 'mouseleave', () => this._hideVersionTooltip());
            this._addListener(versionLink, 'focus', () => this._showVersionTooltip(versionLink));
            this._addListener(versionLink, 'blur', () => this._hideVersionTooltip());
            this._addListener(versionLink, 'click', (e) => {
                e.stopPropagation();
                this._hideVersionTooltip();
            });
        }

        const avatarBtn = document.getElementById('avatar-btn');
        if (avatarBtn) {
            this._addListener(avatarBtn, 'click', () => this.showAvatarModal());
        }

        // Smart routing for Clients link
        const clientsLink = document.getElementById('sidebar-clients-link');
        if (clientsLink) {
            this._addListener(clientsLink, 'click', (e) => {
                // Ctrl/Cmd-click falls through to open-in-new: the browser's
                // native new-tab on the href, or the desktop shell's pop-out
                // handler below (which this handler would otherwise swallow).
                if (e.ctrlKey || e.metaKey) return;
                e.preventDefault();
                e.stopPropagation();
                const viewMode = localStorage.getItem('clientViewMode') || 'profile';
                window.location.hash = viewMode === 'directory' ? '#/clients' : '#/client';
            });
        }

        // Initialize notifications panel
        const notificationsContainer = document.getElementById('notifications-container');
        if (notificationsContainer) {
            notificationsPanel.init(notificationsContainer);
        }

        // Categories container: toggle / section actions / add-group / drag-drop (event delegation)
        const categoriesContainer = document.getElementById('sidebar-categories-container');
        if (categoriesContainer) {
            this._attachCategoriesHandlers(categoriesContainer);
        }

        // Hashchange for active route
        this._addListener(window, 'hashchange', () => this.updateActiveRoute());

        // Desktop shell (Pake) only: Ctrl/Cmd-click or middle-click a sidebar
        // nav link opens that view in its own app window, mirroring how the
        // same gestures open a new browser tab. A normal browser never
        // matches isDesktopShell() and keeps its native new-tab behaviour
        // untouched. The pop-out itself requires the shell to be built with
        // pake-cli's --new-window flag: with it, window.open(<internal url>,
        // '_blank') opens a real second app window; without it Pake rewrites
        // the call to same-window navigation (and app boot shows the
        // reinstall notice - see shellUpdateRequired). Covers the fixed nav
        // and every programmed list (both render as a.sidebar__nav-link).
        if (isDesktopShell()) {
            const sidebarEl = document.getElementById('sidebar');
            if (sidebarEl) {
                const popOut = (e) => {
                    const link = e.target.closest('a.sidebar__nav-link');
                    if (!link) return;
                    e.preventDefault();
                    window.open(link.href, '_blank');
                };
                this._addListener(sidebarEl, 'click', (e) => {
                    if (e.ctrlKey || e.metaKey) popOut(e);
                });
                this._addListener(sidebarEl, 'auxclick', (e) => {
                    if (e.button === 1) popOut(e);
                });
            }
        }
    },

    // ========================================================================
    // CATEGORIES CONTAINER: events + drag-drop
    // ========================================================================

    _attachCategoriesHandlers(container) {
        // Click delegation: toggle, rename, delete, new-group, retry, inline-input commits
        this._addListener(container, 'click', (e) => {
            // Skip clicks while in inline edit/add state on that input itself
            if (e.target.matches('.sidebar__inline-input')) return;

            const toggleBtn = e.target.closest('[data-category-toggle]');
            if (toggleBtn) {
                e.preventDefault();
                this.toggleCategorySection(toggleBtn.dataset.categoryToggle);
                return;
            }

            const sectionAction = e.target.closest('[data-section-action]');
            if (sectionAction) {
                e.preventDefault();
                e.stopPropagation();
                if (sectionAction.dataset.sectionAction === 'menu') {
                    this._openSectionMenu(sectionAction, sectionAction.dataset.categoryId);
                }
                return;
            }

            const newGroupBtn = e.target.closest('[data-action="new-group"]');
            if (newGroupBtn) {
                e.preventDefault();
                this._beginNewGroup();
                return;
            }
        });

        // Drag-and-drop (event delegation, single set of listeners)
        this._dragState = null;
        this._dragRafId = 0;
        this._dragPendingTarget = null;

        this._addListener(container, 'dragstart', (e) => this._handleDragStart(e));
        this._addListener(container, 'dragover', (e) => this._handleDragOver(e));
        this._addListener(container, 'dragleave', (e) => this._handleDragLeave(e));
        this._addListener(container, 'drop', (e) => this._handleDrop(e));
        this._addListener(container, 'dragend', (e) => this._handleDragEnd(e));
    },

    _handleDragStart(e) {
        // Disable drag entirely in fully collapsed sidebar (no room for affordances)
        if (this.isCollapsed) {
            e.preventDefault();
            return;
        }

        const itemEl = e.target.closest('.sidebar__nav-item--draggable');
        const sectionEl = e.target.closest('.sidebar__section-label--draggable');

        if (itemEl) {
            this._dragState = {
                type: 'item',
                listId: itemEl.dataset.listId,
                originGroupId: itemEl.dataset.groupId,
                el: itemEl,
                didDrop: false
            };
            itemEl.classList.add('sidebar__nav-item--dragging');
        } else if (sectionEl) {
            this._dragState = {
                type: 'section',
                categoryId: sectionEl.dataset.categoryId,
                el: sectionEl,
                didDrop: false
            };
            sectionEl.classList.add('sidebar__section-label--dragging');
        } else {
            return;
        }

        e.dataTransfer.effectAllowed = 'move';
        // Firefox needs data set to start the drag
        try { e.dataTransfer.setData('text/plain', ''); } catch (_) { /* ignore */ }

        // Mark the container so CSS can highlight drop zones during drag. The
        // item-specific flag additionally reveals the empty PINNED bucket (the
        // drop-to-uncategorize target) - but only for item drags, never for
        // section reorders.
        const container = document.getElementById('sidebar-categories-container');
        container?.classList.add('sidebar__categories--dragging');
        if (this._dragState.type === 'item') {
            container?.classList.add('sidebar__categories--dragging-item');
        }
    },

    _handleDragOver(e) {
        if (!this._dragState) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = 'move';

        // Cache the event target locally (DOM target stays valid through this turn)
        const target = e.target;
        const clientY = e.clientY;

        this._dragPendingTarget = { target, clientY };

        // Coalesce DOM mutations to once per animation frame
        if (this._dragRafId) return;
        this._dragRafId = requestAnimationFrame(() => {
            this._dragRafId = 0;
            const pending = this._dragPendingTarget;
            this._dragPendingTarget = null;
            if (!pending || !this._dragState) return;
            this._applyDragHover(pending.target, pending.clientY);
        });
    },

    _applyDragHover(target, clientY) {
        if (this._dragState.type === 'item') {
            this._applyItemHover(target, clientY);
        } else if (this._dragState.type === 'section') {
            this._applySectionHover(target, clientY);
        }
    },

    _applyItemHover(target, clientY) {
        const draggedEl = this._dragState.el;
        if (!draggedEl || !draggedEl.isConnected) return;

        // Hovering the source row itself -> not a meaningful drop target
        const overItem = target.closest('.sidebar__nav-item--draggable');
        if (overItem === draggedEl) {
            this._clearDropIndicator();
            this._dragState.pendingDrop = null;
            return;
        }

        // Hovering an item -> indicator goes above/below based on midpoint
        if (overItem) {
            const list = overItem.parentElement;
            if (!list || !list.matches('[data-category-nav]')) return;
            const rect = overItem.getBoundingClientRect();
            const midY = rect.top + rect.height / 2;
            const beforeEl = clientY < midY ? overItem : overItem.nextSibling;
            this._setItemDropPlacement(list, beforeEl);
            return;
        }

        // Hovering an empty placeholder or the nav itself -> indicator at end
        const overNav = target.closest('[data-category-nav]');
        if (overNav) {
            this._setItemDropPlacement(overNav, null);
            return;
        }

        // Hovering a section header -> drop into that section. A collapsed
        // category is NOT auto-expanded (popping it open mid-drag was jarring);
        // instead we glow the header itself as the drop target and queue an
        // append into its still-collapsed nav. The item lands there and stays
        // tucked away until the user opens the group. An expanded category keeps
        // the inline indicator line at its first row.
        const overSection = target.closest('.sidebar__section-label[data-category-id]');
        if (overSection) {
            const sectionId = overSection.dataset.categoryId;
            const targetNav = document.querySelector(`[data-category-nav="${CSS.escape(sectionId)}"]`);
            if (!targetNav) {
                this._clearDropIndicator();
                this._dragState.pendingDrop = null;
                return;
            }
            if (this._isSectionCollapsed(sectionId)) {
                this._clearDropIndicator();
                this._setHeaderDropTarget(overSection);
                this._dragState.pendingDrop = { kind: 'item', parentNav: targetNav, beforeEl: null };
            } else {
                const firstReal = targetNav.querySelector('.sidebar__nav-item--draggable');
                this._setItemDropPlacement(targetNav, firstReal);
            }
            return;
        }

        // No valid target - hide the indicator
        this._clearDropIndicator();
        this._dragState.pendingDrop = null;
    },

    // Glow a (collapsed) category header as the active drop target while an item
    // is dragged over it. Mutually exclusive with the inline drop line - only
    // one drop affordance is ever shown at once.
    _setHeaderDropTarget(headerEl) {
        if (this._dropTargetHeader === headerEl) return;
        this._clearHeaderDropTarget();
        headerEl.classList.add('sidebar__section-label--drop-target');
        this._dropTargetHeader = headerEl;
    },

    _clearHeaderDropTarget() {
        if (this._dropTargetHeader) {
            this._dropTargetHeader.classList.remove('sidebar__section-label--drop-target');
            this._dropTargetHeader = null;
        }
    },

    _applySectionHover(target, clientY) {
        const draggedLabel = this._dragState.el;
        if (!draggedLabel || !draggedLabel.isConnected) return;

        const overLabel = target.closest('.sidebar__section-label--draggable');
        if (!overLabel || overLabel === draggedLabel) {
            this._clearDropIndicator();
            this._dragState.pendingDrop = null;
            return;
        }

        const parent = overLabel.parentElement;
        const overNav = overLabel.nextElementSibling;
        if (!parent || !overNav) return;

        const rect = overLabel.getBoundingClientRect();
        const midY = rect.top + rect.height / 2;
        const beforeNode = clientY < midY ? overLabel : overNav.nextSibling;
        this._setSectionDropPlacement(parent, beforeNode);
    },

    _setItemDropPlacement(parentNav, beforeEl) {
        // Showing the inline line cancels any collapsed-header drop glow.
        this._clearHeaderDropTarget();

        // Filter out the indicator and the empty placeholder so we never
        // reference them as drop targets.
        if (beforeEl && beforeEl === this._dropIndicator) beforeEl = beforeEl.nextSibling;
        if (beforeEl?.classList?.contains('sidebar__nav-item--empty')) beforeEl = null;

        // Treat a position adjacent to the dragged element (within the same
        // list) as a no-op so the indicator does not appear on either side
        // of the source row.
        const draggedEl = this._dragState.el;
        if (draggedEl && draggedEl.parentElement === parentNav) {
            if (beforeEl === draggedEl || beforeEl === draggedEl.nextSibling) {
                this._clearDropIndicator();
                this._dragState.pendingDrop = null;
                return;
            }
        }

        this._dragState.pendingDrop = { kind: 'item', parentNav, beforeEl };

        // Place the indicator. If beforeEl is null, append, but stay above
        // any empty placeholder so the line sits where the drop will land.
        const line = this._ensureDropIndicator();
        let insertionTarget = beforeEl;
        if (!insertionTarget) {
            insertionTarget = parentNav.querySelector('.sidebar__nav-item--empty') || null;
        }

        if (insertionTarget) {
            if (line === insertionTarget.previousSibling) return;
            parentNav.insertBefore(line, insertionTarget);
        } else {
            if (parentNav.lastChild === line) return;
            parentNav.appendChild(line);
        }
    },

    _setSectionDropPlacement(parent, beforeNode) {
        if (beforeNode === this._dropIndicator) beforeNode = beforeNode.nextSibling;

        // Suppress no-op placement adjacent to the dragged section block
        // (label + its following nav).
        const draggedLabel = this._dragState.el;
        const draggedNav = draggedLabel?.nextElementSibling || null;
        if (draggedLabel && draggedNav) {
            if (beforeNode === draggedLabel || beforeNode === draggedNav.nextSibling) {
                this._clearDropIndicator();
                this._dragState.pendingDrop = null;
                return;
            }
        }

        this._dragState.pendingDrop = { kind: 'section', parent, beforeNode };

        // Render indicator in the container; clamp to the "Add group" footer
        // so the line never falls below it.
        const line = this._ensureDropIndicator();
        const footer = parent.querySelector('[data-add-group]');
        const target = beforeNode || footer || null;

        if (target) {
            if (line === target.previousSibling) return;
            parent.insertBefore(line, target);
        } else {
            if (parent.lastChild === line) return;
            parent.appendChild(line);
        }
    },

    _ensureDropIndicator() {
        if (this._dropIndicator) return this._dropIndicator;
        const el = document.createElement('li');
        el.className = 'sidebar__drop-indicator';
        el.setAttribute('aria-hidden', 'true');
        this._dropIndicator = el;
        return el;
    },

    _clearDropIndicator() {
        if (this._dropIndicator) {
            this._dropIndicator.remove();
            this._dropIndicator = null;
        }
        // The line and the collapsed-header glow are the two drop affordances;
        // clearing one always clears the other so they can never both linger.
        this._clearHeaderDropTarget();
    },

    _handleDragLeave(e) {
        if (!this._dragState) return;
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;
        const related = e.relatedTarget;
        if (!related || !container.contains(related)) {
            this._clearDropIndicator();
            this._dragState.pendingDrop = null;
        }
    },

    _handleDrop(e) {
        if (!this._dragState) return;
        e.preventDefault();
        this._dragState.didDrop = true;

        const pending = this._dragState.pendingDrop;
        if (!pending) return;

        if (pending.kind === 'item') {
            const { parentNav, beforeEl } = pending;
            if (!parentNav?.isConnected) return;
            parentNav.querySelector('.sidebar__nav-item--empty')?.remove();
            const finalBefore = beforeEl && beforeEl.parentElement === parentNav ? beforeEl : null;
            if (finalBefore) {
                parentNav.insertBefore(this._dragState.el, finalBefore);
            } else {
                parentNav.appendChild(this._dragState.el);
            }
        } else if (pending.kind === 'section') {
            const { parent, beforeNode } = pending;
            const draggedLabel = this._dragState.el;
            const draggedNav = draggedLabel?.nextElementSibling;
            if (!parent?.isConnected || !draggedNav) return;
            const footer = parent.querySelector('[data-add-group]');
            const finalBefore = beforeNode && beforeNode.parentElement === parent ? beforeNode : footer || null;
            if (finalBefore) {
                parent.insertBefore(draggedLabel, finalBefore);
                parent.insertBefore(draggedNav, finalBefore);
            } else {
                parent.appendChild(draggedLabel);
                parent.appendChild(draggedNav);
            }
        }
    },

    _handleDragEnd(_e) {
        if (this._dragRafId) {
            cancelAnimationFrame(this._dragRafId);
            this._dragRafId = 0;
        }
        this._dragPendingTarget = null;

        const state = this._dragState;
        this._dragState = null;

        this._clearDropIndicator();

        const container = document.getElementById('sidebar-categories-container');
        container?.classList.remove('sidebar__categories--dragging');
        container?.classList.remove('sidebar__categories--dragging-item');

        if (!state) return;

        state.el?.classList.remove('sidebar__nav-item--dragging');
        state.el?.classList.remove('sidebar__section-label--dragging');

        // Without a drop, the DOM was never mutated - nothing to persist or revert.
        if (!state.didDrop) return;

        if (state.type === 'item') {
            // Sync placeholders + the pinned-empty state NOW (synchronously),
            // before the network save. Otherwise a list just dropped into the
            // (revealed) empty PINNED bucket would vanish - the bucket still
            // carries --pinned-empty and collapses the moment --dragging-item
            // is removed above - until the async save resolved and refreshed it.
            this._refreshEmptyPlaceholders();
            this._persistItemDrag(state);
        } else if (state.type === 'section') {
            this._persistSectionDrag();
        }
    },

    // ========================================================================
    // PERSISTENCE - optimistic save
    // ========================================================================

    async _persistItemDrag(state) {
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;

        const config = getSidebarConfig();
        const before = JSON.stringify({ a: config.assignments, o: config.listOrders });

        // Walk the DOM in display order and rebuild assignments + listOrders.
        const navs = container.querySelectorAll('[data-category-nav]');
        const newAssignments = { ...config.assignments };
        const newListOrders = {};
        let orderIdx = 0;

        navs.forEach(nav => {
            const groupId = nav.dataset.categoryNav;
            nav.querySelectorAll('.sidebar__nav-item--draggable').forEach(item => {
                const listId = item.dataset.listId;
                if (!listId) return;

                if (groupId === PINNED_GROUP_ID) {
                    delete newAssignments[listId];
                } else {
                    newAssignments[listId] = groupId;
                }
                newListOrders[listId] = orderIdx++;
                // Keep DOM-attribute in sync so re-renders reflect new group
                item.dataset.groupId = groupId;
            });
        });

        // No-op if nothing actually changed (mouse-up without movement)
        const after = JSON.stringify({ a: newAssignments, o: newListOrders });
        if (before === after) return;

        const newConfig = { ...config, assignments: newAssignments, listOrders: newListOrders };

        // Optimistic: profile cache already represents new state through getSidebarConfig
        // when we save. Persist; on failure, revert + re-render from server truth.
        const result = await updateSidebarConfig(newConfig);
        if (!result.success) {
            showToast('Failed to save sidebar order', 'error');
            this.renderCustomMenuItems();
        }
        // On success the optimistic DOM is already correct: placeholders and the
        // pinned-empty state were refreshed synchronously in _handleDragEnd
        // before this save kicked off, so there is nothing more to do here.
    },

    async _persistSectionDrag() {
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;

        const config = getSidebarConfig();
        const newOrder = [...container.querySelectorAll('.sidebar__section-label--draggable')]
            .map(el => el.dataset.categoryId)
            .filter(Boolean);

        const currentOrder = [...config.categories]
            .sort((a, b) => a.order - b.order)
            .map(c => c.id);

        if (newOrder.join('|') === currentOrder.join('|')) return;

        const catMap = new Map(config.categories.map(c => [c.id, c]));
        const newCategories = newOrder
            .map(id => catMap.get(id))
            .filter(Boolean)
            .map((cat, i) => ({ ...cat, order: i }));

        const newConfig = { ...config, categories: newCategories };
        const result = await updateSidebarConfig(newConfig);
        if (!result.success) {
            showToast('Failed to save group order', 'error');
            this.renderCustomMenuItems();
        }
    },

    _refreshEmptyPlaceholders() {
        // After drops, some navs may have items removed/added - sync the empty placeholder
        const container = document.getElementById('sidebar-categories-container');
        if (!container) return;
        container.querySelectorAll('[data-category-nav]').forEach(nav => {
            const isPinned = nav.dataset.categoryNav === PINNED_GROUP_ID;
            const hasItems = nav.querySelector('.sidebar__nav-item--draggable');
            const placeholder = nav.querySelector('.sidebar__nav-item--empty');

            if (!hasItems && !placeholder) {
                const li = document.createElement('li');
                li.className = 'sidebar__nav-item sidebar__nav-item--empty';
                li.setAttribute('aria-hidden', 'true');
                li.innerHTML = `<span class="sidebar__nav-empty-text">${isPinned ? 'No uncategorized lists' : 'Drop a list here'}</span>`;
                nav.appendChild(li);
            } else if (hasItems && placeholder) {
                placeholder.remove();
            }

            // Keep the PINNED bucket's hidden-when-empty state in sync after a
            // drop (it only shows when it holds items, or transiently mid-drag).
            if (isPinned) {
                const label = container.querySelector(
                    `.sidebar__section-label[data-category-id="${PINNED_GROUP_ID}"]`
                );
                nav.classList.toggle('sidebar__nav--pinned-empty', !hasItems);
                label?.classList.toggle('sidebar__section-label--pinned-empty', !hasItems);
            }
        });
    },

    // ========================================================================
    // GROUP LIFECYCLE - add / rename / delete (inline)
    // ========================================================================

    /**
     * Track the single active inline IconPicker so it is always torn down,
     * even if the sidebar re-renders or disposes while an edit is open. Any
     * previously-tracked picker is destroyed first (only one inline edit at a
     * time). IconPicker.destroy() is idempotent, so a later local destroy is safe.
     */
    _registerInlinePicker(picker) {
        if (this._activeInlinePicker && this._activeInlinePicker !== picker) {
            this._activeInlinePicker.destroy();
        }
        this._activeInlinePicker = picker;
    },

    _destroyActiveInlinePicker() {
        if (this._activeInlinePicker) {
            this._activeInlinePicker.destroy();
            this._activeInlinePicker = null;
        }
    },

    /**
     * Open the per-group "more options" dropdown (Rename / Delete) anchored to
     * the kebab button. Reuses the shared createMenu chrome (positioning +
     * outside-click + escape) with baseClass:false so the element carries ONLY
     * .sidebar__action-menu (not the data-grid base class, which is loaded
     * globally and would otherwise override its z-index/padding) and is styled
     * fully self-contained from shell.css. The kebab is passed as `trigger` so
     * the outside-click ignores it and we own a clean toggle.
     */
    _openSectionMenu(triggerEl, categoryId) {
        // Re-clicking the kebab that owns the open menu toggles it shut. Because
        // createMenu ignores `trigger` in its outside-click, that click does not
        // auto-close - so the menu is still open here and we close it ourselves.
        if (this._sectionMenu && this._sectionMenuTrigger === triggerEl) {
            this._sectionMenu.destroy();
            return;
        }
        // Switching kebabs: tear down any other open menu first.
        if (this._sectionMenu) this._sectionMenu.destroy();

        triggerEl.setAttribute('aria-expanded', 'true');
        this._sectionMenuTrigger = triggerEl;

        const rect = triggerEl.getBoundingClientRect();
        const bodyHtml = `
            <button type="button" class="sidebar__action-menu-item" data-menu-action="rename">
                <i class="fas fa-pen"></i><span>Rename</span>
            </button>
            <button type="button" class="sidebar__action-menu-item sidebar__action-menu-item--danger" data-menu-action="delete">
                <i class="fas fa-trash"></i><span>Delete</span>
            </button>
        `;

        const menu = createMenu({
            type: 'context',
            bodyHtml,
            anchor: { x: rect.right, y: rect.bottom + 4 },
            placement: 'point',
            extraClass: 'sidebar__action-menu',
            baseClass: false,
            trigger: triggerEl,
            onClose: () => {
                this._sectionMenu = null;
                this._sectionMenuTrigger = null;
                triggerEl.setAttribute('aria-expanded', 'false');
            }
        });

        // The click handler lives on the menu element itself, which createMenu
        // removes from the DOM on close - so it is GC'd with the menu rather
        // than pinned in the long-lived sidebar EventManager.
        menu.el.addEventListener('click', (e) => {
            const item = e.target.closest('[data-menu-action]');
            if (!item) return;
            const action = item.dataset.menuAction;
            menu.destroy();
            if (action === 'rename') this._beginRenameGroup(categoryId);
            else if (action === 'delete') this._handleDeleteGroup(categoryId);
        });

        menu.show();

        // Right-align the menu under the kebab so it hugs the sidebar rather
        // than spilling into the page content. createMenu already clamped it to
        // the viewport; we only refine the horizontal edge.
        const menuRect = rectToLayout(menu.el.getBoundingClientRect());
        menu.el.style.left = `${Math.max(8, toLayoutPx(rect.right) - menuRect.width)}px`;

        this._sectionMenu = menu;
    },

    async _beginNewGroup() {
        const footer = document.querySelector('[data-add-group]');
        if (!footer) return;

        footer.innerHTML = `
            <div class="sidebar__inline-row">
                <div class="sidebar__inline-picker" data-inline-picker></div>
                <input type="text" class="sidebar__inline-input" placeholder="Group name" maxlength="40" />
            </div>
        `;
        const input = footer.querySelector('.sidebar__inline-input');
        const pickerHost = footer.querySelector('[data-inline-picker]');
        if (!input || !pickerHost) return;

        // Compact icon picker seeded with the default group icon. Destroyed in
        // every exit path below so no listeners or DOM leak. Loaded lazily so the
        // ~1500-icon catalogue stays out of the boot bundle - it is only needed
        // once a user actually opens this inline editor.
        const { IconPicker } = await import('shared/components/icon-picker/icon-picker.js');
        const picker = new IconPicker({
            container: pickerHost,
            selectedIcon: 'fa-folder',
            searchable: true,
            variant: 'inline',
            onSelect: () => { if (input.isConnected) input.focus(); }
        });
        this._registerInlinePicker(picker);

        input.focus();

        let done = false;
        const cleanup = () => {
            if (done) return;
            done = true;
            input.removeEventListener('keydown', onKey);
            input.removeEventListener('blur', onBlur);
            this._destroyActiveInlinePicker();
            // Restore the footer button (renderCustomMenuItems re-renders on save)
            footer.innerHTML = `
                <button type="button" class="sidebar__add-group-btn" data-action="new-group">
                    <i class="fas fa-plus"></i>
                    <span class="sidebar__add-group-text">New group</span>
                </button>
            `;
        };

        let pending = false;
        const commit = async () => {
            if (pending || done) return;
            pending = true;
            const name = input.value.trim();
            if (!name) { cleanup(); return; }
            input.disabled = true;
            const result = await addSidebarCategory(name, picker.getValue());
            if (!result.success) {
                showToast(result.error || 'Failed to add group', 'error');
                input.disabled = false;
                input.focus();
                pending = false;
                return;
            }
            // Tear down listeners + picker, then re-render the section list
            this._destroyActiveInlinePicker();
            input.removeEventListener('keydown', onKey);
            input.removeEventListener('blur', onBlur);
            done = true;
            this.renderCustomMenuItems();
        };

        const onKey = (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); cleanup(); }
        };
        const onBlur = () => {
            // Defer so Enter-via-blur doesn't double-fire with keydown, and so a
            // click on the picker dropdown (which steals focus) doesn't cancel.
            setTimeout(() => {
                if (done) return;
                // Keep editing while the picker dropdown is open / focus moved into it
                if (pickerHost.contains(document.activeElement) || picker.isOpen) {
                    if (input.isConnected) input.focus();
                    return;
                }
                if (input.isConnected) commit();
            }, 0);
        };

        input.addEventListener('keydown', onKey);
        input.addEventListener('blur', onBlur);
    },

    async _beginRenameGroup(categoryId) {
        const labelEl = document.querySelector(`.sidebar__section-label[data-category-id="${CSS.escape(categoryId)}"]`);
        if (!labelEl) return;
        const textEl = labelEl.querySelector('.sidebar__section-label-text');
        if (!textEl) return;
        const iconEl = labelEl.querySelector('.sidebar__section-icon');

        const original = textEl.textContent;
        const currentIcon = getSidebarConfig().categories.find(c => c.id === categoryId)?.icon || 'fa-folder';

        // Compact icon picker swapped in where the static section icon sat
        const pickerHost = document.createElement('div');
        pickerHost.className = 'sidebar__inline-picker';
        pickerHost.setAttribute('data-inline-picker', '');

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'sidebar__inline-input sidebar__inline-input--inline';
        input.value = original;
        input.maxLength = 40;

        // Block dragstart while editing so clicks/selection work
        labelEl.removeAttribute('draggable');

        // Swap icon -> picker host (or insert picker host before the text if no icon)
        if (iconEl) iconEl.replaceWith(pickerHost);
        else labelEl.insertBefore(pickerHost, textEl);
        textEl.replaceWith(input);

        // Lazy-load the picker so the icon catalogue stays out of the boot bundle.
        const { IconPicker } = await import('shared/components/icon-picker/icon-picker.js');
        const picker = new IconPicker({
            container: pickerHost,
            selectedIcon: currentIcon,
            searchable: true,
            variant: 'inline',
            onSelect: () => { if (input.isConnected) input.focus(); }
        });
        this._registerInlinePicker(picker);

        input.focus();
        input.select();

        let done = false;
        // Rebuild the label's leading icon + name span and tear the editor down.
        const restore = (text, icon) => {
            if (done) return;
            done = true;
            input.removeEventListener('keydown', onKey);
            input.removeEventListener('blur', onBlur);
            this._destroyActiveInlinePicker();

            const newIconEl = document.createElement('span');
            newIconEl.innerHTML = renderIcon(icon || 'fa-folder', 'sidebar__section-icon');
            const renderedIcon = newIconEl.firstElementChild;
            if (renderedIcon) pickerHost.replaceWith(renderedIcon);
            else pickerHost.remove();

            const span = document.createElement('span');
            span.className = 'sidebar__section-label-text';
            span.textContent = text;
            input.replaceWith(span);
            labelEl.setAttribute('draggable', 'true');
        };

        let pending = false;
        const commit = async () => {
            if (pending || done) return;
            pending = true;
            const newName = input.value.trim();
            const newIcon = picker.getValue();
            // Nothing changed -> restore the original name + icon
            if ((!newName || newName === original) && newIcon === currentIcon) {
                restore(original, currentIcon);
                return;
            }
            if (!newName) { restore(original, currentIcon); return; }
            input.disabled = true;
            const result = await renameSidebarCategory(categoryId, newName, newIcon);
            if (!result.success) {
                showToast(result.error || 'Failed to rename group', 'error');
                pending = false;
                input.disabled = false;
                restore(original, currentIcon);
                return;
            }
            restore(newName, newIcon);
        };

        const onKey = (e) => {
            if (e.key === 'Enter') { e.preventDefault(); commit(); }
            else if (e.key === 'Escape') { e.preventDefault(); restore(original, currentIcon); }
        };
        const onBlur = () => {
            setTimeout(() => {
                if (done) return;
                // Keep editing while the picker dropdown is open / focus is inside it
                if (pickerHost.contains(document.activeElement) || picker.isOpen) {
                    if (input.isConnected) input.focus();
                    return;
                }
                if (input.isConnected) commit();
            }, 0);
        };

        input.addEventListener('keydown', onKey);
        input.addEventListener('blur', onBlur);
    },

    async _handleDeleteGroup(categoryId) {
        const config = getSidebarConfig();
        const cat = config.categories.find(c => c.id === categoryId);
        if (!cat) return;

        const confirmed = await modal.confirm({
            title: 'Delete group',
            message: `Delete "${cat.name}"? Lists in this group will move back to Pinned. This can't be undone.`,
            confirmText: 'Delete',
            kind: 'danger'
        });
        if (!confirmed) return;

        const result = await deleteSidebarCategory(categoryId);
        if (!result.success) {
            showToast(result.error || 'Failed to delete group', 'error');
            return;
        }
        showToast('Group deleted', 'success');
        this.renderCustomMenuItems();
    },

    // User categories start collapsed (minimized) by default so the rail stays
    // compact; the uncategorized PINNED bucket stays expanded so stray pins are
    // always visible. An explicit stored boolean (set by toggling) overrides the
    // default either way.
    _isSectionCollapsed(sectionId) {
        const stored = this.categoryCollapseStates[sectionId];
        if (stored === undefined) return sectionId !== PINNED_GROUP_ID;
        return stored === true;
    },

    toggleCategorySection(categoryId) {
        this.categoryCollapseStates[categoryId] = !this._isSectionCollapsed(categoryId);
        localStorage.setItem('sidebar-category-collapsed', JSON.stringify(this.categoryCollapseStates));
        this.syncCategoryCollapsedState(categoryId);
    },

    syncCategoryCollapsedState(categoryId) {
        const label = document.querySelector(`[data-category-id="${categoryId}"]`);
        const nav = document.querySelector(`[data-category-nav="${categoryId}"]`);
        const isCollapsed = this._isSectionCollapsed(categoryId);

        label?.classList.toggle('sidebar__section-label--collapsed', isCollapsed);
        nav?.classList.toggle('sidebar__nav--collapsed', isCollapsed);

        // Update toggle button title
        const toggle = label?.querySelector('[data-category-toggle]');
        if (toggle) toggle.title = isCollapsed ? 'Expand' : 'Collapse';
    },

    showAvatarModal() {
        const { userName, initials, avatarUrl } = this._getAvatarData();

        const bodyContent = `
            <div class="avatar-modal__body">
                <div class="avatar-modal__preview">
                    ${avatarUrl
                        ? `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(userName)}" class="avatar-modal__image" />`
                        : `<div class="avatar-modal__initials">${initials}</div>`
                    }
                    <div class="avatar-modal__overlay">
                        <i class="fas fa-camera"></i>
                    </div>
                </div>
                <input type="file" id="avatar-file-input" accept="image/jpeg,image/png,image/gif,image/webp" hidden />
                <p class="avatar-modal__hint">
                    Supported formats: JPEG, PNG, GIF, WebP. Max size: 2MB.
                </p>
            </div>
        `;

        const actions = [
            {
                label: 'Upload Photo',
                kind: 'primary',
                id: 'avatar-upload-btn',
                onClick: () => {
                    const fileInput = document.getElementById('avatar-file-input');
                    if (fileInput) fileInput.click();
                }
            }
        ];

        if (avatarUrl) {
            actions.unshift({
                label: 'Remove',
                kind: 'secondary',
                id: 'avatar-remove-btn',
                onClick: async () => {
                    const removeBtn = document.getElementById('avatar-remove-btn');
                    if (removeBtn) {
                        removeBtn.disabled = true;
                        removeBtn.textContent = 'Removing...';
                    }

                    const result = await removeAvatar();

                    if (result.success) {
                        showToast('Profile picture removed', 'success');
                        modal.close();
                        this.refreshAvatar();
                    } else {
                        showToast(result.error || 'Failed to remove', 'error');
                        if (removeBtn) {
                            removeBtn.disabled = false;
                            removeBtn.textContent = 'Remove';
                        }
                    }
                }
            });
        }

        modal.open({
            title: 'Profile Picture',
            content: bodyContent,
            size: 'sm',
            actions,
            onOpen: () => {
                const fileInput = document.getElementById('avatar-file-input');
                if (fileInput) {
                    fileInput.addEventListener('change', async (e) => {
                        const file = e.target.files[0];
                        if (!file) return;

                        const uploadBtn = document.getElementById('avatar-upload-btn');
                        if (uploadBtn) {
                            uploadBtn.disabled = true;
                            uploadBtn.textContent = 'Uploading...';
                        }

                        const result = await uploadAvatar(file);

                        if (result.success) {
                            showToast('Profile picture updated', 'success');
                            modal.close();
                            this.refreshAvatar();
                        } else {
                            showToast(result.error || 'Failed to upload', 'error');
                            if (uploadBtn) {
                                uploadBtn.disabled = false;
                                uploadBtn.textContent = 'Upload Photo';
                            }
                        }
                    });
                }
            }
        });
    },

    refreshAvatar() {
        // Update just the avatar section without reloading everything
        const avatarContainer = document.getElementById('avatar-btn');
        if (!avatarContainer) return;

        const { userName, initials, avatarUrl } = this._getAvatarData();

        avatarContainer.innerHTML = avatarUrl
            ? `<img src="${escapeHtml(avatarUrl)}" alt="${escapeHtml(userName)}" class="sidebar__avatar-img" />`
            : initials;
    },

    toggleCollapsed() {
        this.isCollapsed = !this.isCollapsed;
        localStorage.setItem('sidebar-collapsed', this.isCollapsed);
        this.syncCollapsedState();
    },

    syncCollapsedState() {
        const sidebar = document.getElementById('sidebar');
        const app = document.getElementById('app');

        if (this.isCollapsed) {
            sidebar?.classList.add('sidebar--collapsed');
            app?.classList.add('app--sidebar-collapsed');
        } else {
            sidebar?.classList.remove('sidebar--collapsed');
            app?.classList.remove('app--sidebar-collapsed');
        }
    },

    // ========================================================================
    // VERSION TAG - floating changelog tooltip
    // ========================================================================

    /**
     * Show a changelog tooltip anchored to the version tag. It is rendered into
     * <body> with position:fixed (set here) so it escapes the sidebar's
     * overflow:hidden and layers above it. Built fresh on each hover and removed
     * on leave, so no DOM lingers; dispose() also clears any active one.
     */
    _showVersionTooltip(anchor) {
        this._hideVersionTooltip();
        if (!anchor?.isConnected) return;

        const changes = getRecentChanges(4);
        if (!changes.length) return;

        const tip = document.createElement('div');
        tip.className = 'sidebar__version-tooltip';
        tip.setAttribute('role', 'tooltip');
        tip.innerHTML = `
            <div class="sidebar__version-tooltip-title">Recent updates</div>
            <div class="sidebar__version-tooltip-list">
                ${changes.map(c => `
                    <div class="sidebar__version-tooltip-item">
                        <span class="sidebar__version-tooltip-ver">V${escapeHtml(c.version)}</span>
                        <span class="sidebar__version-tooltip-desc">${escapeHtml(c.summary)}</span>
                    </div>
                `).join('')}
            </div>
            <div class="sidebar__version-tooltip-foot">
                <i class="fas fa-book-open" aria-hidden="true"></i>
                <span>Click to open the full changelog</span>
            </div>
        `;
        document.body.appendChild(tip);
        this._versionTooltip = tip;

        // Position just below the tag, left-aligned, then clamp into the
        // viewport. Reading getBoundingClientRect() also forces the layout flush
        // that lets the --visible transition animate on this fresh element.
        const anchorRect = rectToLayout(anchor.getBoundingClientRect());
        const tipRect = rectToLayout(tip.getBoundingClientRect());
        const gap = 8;
        let left = anchorRect.left;
        let top = anchorRect.bottom + gap;
        left = Math.min(left, toLayoutPx(window.innerWidth) - tipRect.width - gap);
        left = Math.max(gap, left);
        if (top + tipRect.height > toLayoutPx(window.innerHeight) - gap) {
            top = Math.max(gap, anchorRect.top - tipRect.height - gap);
        }
        tip.style.left = `${Math.round(left)}px`;
        tip.style.top = `${Math.round(top)}px`;

        tip.classList.add('sidebar__version-tooltip--visible');
    },

    _hideVersionTooltip() {
        if (this._versionTooltip) {
            this._versionTooltip.remove();
            this._versionTooltip = null;
        }
    },

    updateActiveRoute() {
        const currentHash = window.location.hash.slice(1) || '/';
        const currentPath = currentHash.split('?')[0]; // Get path without query params

        // Identify the custom list currently shown (if any) so a pinned list
        // stays highlighted whether it was opened via its slug (#/afs) or the id
        // form (#/list-view?id=...). Slug -> id is resolved from the pinned set.
        let activeListId = null;
        if (currentPath === '/list-view') {
            activeListId = new URLSearchParams(currentHash.split('?')[1] || '').get('id');
        } else {
            const slug = currentPath.replace(/^\//, '').toLowerCase();
            if (slug) {
                activeListId = this.pinnedTaskLists.find(
                    l => (l.slug || '').toLowerCase() === slug
                )?.id || null;
            }
        }

        document.querySelectorAll('.sidebar__nav-link').forEach((link) => {
            const route = link.getAttribute('data-route');
            const linkHref = link.getAttribute('href');

            // Custom-list links: authoritative match by list id, which covers
            // both URL forms even when they disagree (e.g. opened via id while
            // the sidebar link carries the slug).
            const liListId = link.closest('.sidebar__nav-item')?.dataset.listId;
            if (activeListId && liListId && liListId === activeListId) {
                link.classList.add('sidebar__nav-link--active');
                return;
            }

            // For links with query parameters, compare the full href
            if (linkHref && linkHref.includes('?')) {
                const linkHash = linkHref.replace('#', '');
                if (linkHash === currentHash) {
                    link.classList.add('sidebar__nav-link--active');
                } else {
                    link.classList.remove('sidebar__nav-link--active');
                }
            } else {
                // Clients link highlights for both /client and /clients
                if (route === '/clients' && (currentPath === '/client' || currentPath === '/clients')) {
                    link.classList.add('sidebar__nav-link--active');
                } else if (route === currentHash || route === currentPath) {
                    link.classList.add('sidebar__nav-link--active');
                } else {
                    link.classList.remove('sidebar__nav-link--active');
                }
            }
        });
    },

    /**
     * Setup progressive name truncation
     * Shows first name → just avatar if first name overflows
     */
    setupNameTruncation() {
        const nameEl = document.querySelector('.sidebar__user-name');
        if (!nameEl) return;

        const checkTruncation = () => {
            const firstName = nameEl.dataset.firstName || '';

            // Always show first name
            nameEl.textContent = firstName;
            nameEl.classList.remove('sidebar__user-name--hidden');

            // If first name overflows, hide name entirely
            if (nameEl.scrollWidth > nameEl.clientWidth) {
                nameEl.classList.add('sidebar__user-name--hidden');
            }
        };

        // Run initial check
        checkTruncation();

        // Debounced resize handler
        this._addListener(window, 'resize', debounce(checkTruncation, 150));
    },

    /**
     * Dispose of event listeners to prevent memory leaks
     */
    dispose() {
        // Release every DOM listener, timer, and tracked resource in one call
        this.events.dispose();
        this.events = new EventManager('Sidebar');

        // Cancel any pending drag rAF
        if (this._dragRafId) {
            cancelAnimationFrame(this._dragRafId);
            this._dragRafId = 0;
        }
        this._dragState = null;
        this._dragPendingTarget = null;
        if (this._dropIndicator) {
            this._dropIndicator.remove();
            this._dropIndicator = null;
        }
        this._clearHeaderDropTarget();

        // Tear down any open group "more options" dropdown (document listeners)
        if (this._sectionMenu) {
            this._sectionMenu.destroy();
            this._sectionMenu = null;
        }
        this._sectionMenuTrigger = null;

        // Tear down any open inline-edit icon picker (document-level listeners)
        this._destroyActiveInlinePicker();

        // Remove any floating version tooltip appended to <body>
        this._hideVersionTooltip();

        // Clean up resource icons
        resourceIcons.cleanup();

        // Clean up notifications panel
        notificationsPanel.destroy();

        // Reset state
        this.pinnedTaskLists = [];
        this.categoryCollapseStates = {};
    }
};
